import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { DebugableService } from '../debug/debug.type';
import { AuthService } from '../auth/auth.service';
import {
  ClientAuthEvent,
  ClientHeartbeatEvent,
  ClientHelloEvent,
  ClientSubscribeEvent,
  ClientUnsubscribeEvent,
  FluidPrefixType,
  RealtimeEventType,
  SystemAckEvent,
  SystemErrorEvent,
  SystemHelloEvent,
  REALTIME_EVENT_MAP,
} from '@flux/core/client';
import type * as pkg from '@flux/core/client';
import { debounceTime, Subject } from 'rxjs';
import { environment } from '@/environments/environment';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { FeatureFlagsEnum } from '@/generated/featureFlags.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { safeJsonParse } from '@core/utils/safe-json-parse';
import { fluxLogger } from '../logger/logger.instance';

const logger = fluxLogger.extend('socket');
const eventLogger = logger.extend('event');

type core = typeof pkg;

type ClassType<T> = T extends abstract new (...args: any) => infer R ? R : never;

export type RealtimeEventMap = {
  [key in RealtimeEventType]: core extends Record<`${key}Event`, infer T> ? ClassType<T> : never;
};

export interface RealtimeEventPayload<O extends RealtimeEventType> {
  op: O;
  data: RealtimeEventMap[O];
}

@Injectable({ providedIn: 'root' })
export class RealtimeService implements DebugableService, OnDestroy {
  public static isEventFromType<O extends RealtimeEventType>(
    event: RealtimeEventPayload<any>,
    type: O,
  ): event is RealtimeEventPayload<O> {
    return event.op === type;
  }

  public static isOneOfEventTypes<O extends RealtimeEventType>(
    event: RealtimeEventPayload<any>,
    types: O[],
  ): event is RealtimeEventPayload<O> {
    return types.includes(event.op as O);
  }

  private ws: WebSocket | null = null;
  private interval: any = null;

  private retryAttempts = 0;
  private maxRetryAttempts = 5;

  private debugMessages: unknown[] = [];
  private debugConnectionDetails: {
    connectedAt: Date | null;
    disconnectedAt: Date | null;
    firstMessageAt: Date | null;
    lastMessageAt: Date | null;
  }[] = [];

  private helloId: string | null = null;

  public subscriptions = new Set<string>();

  private readonly subscribeEvents: Record<
    string,
    { id: string; type: 'add' | 'remove'; fluids: FluidPrefixType[] }
  > = {};

  public readonly eventReceived$ = new Subject<RealtimeEventPayload<RealtimeEventType>>();

  private lastConnectedToken: string | null = null;

  private connectionState: 'connecting' | 'connected' | 'disconnected' = 'disconnected';

  private queue: ((instance: RealtimeService) => void | Promise<void>)[] = [];

  public constructor(
    private readonly authService: AuthService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.initializeAuthSubscription();
    this.initializeConnection();
  }

  public ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.eventReceived$.complete();
  }

  private initializeAuthSubscription() {
    this.authService.authenticatedUser$
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        logger.info('Auth state changed.', {
          hasUser: Boolean(user),
        });
        if (user && user.realtimeToken && this.lastConnectedToken !== user.realtimeToken) {
          this.lastConnectedToken = user.realtimeToken;
          void this.connect(user.realtimeToken);
          return;
        }

        if (!user && this.lastConnectedToken) {
          logger.warn('User is not authenticated. Disconnecting WebSocket.');
          this.disconnect();
          this.lastConnectedToken = null;
          return;
        }

        if (!user?.realtimeToken) {
          logger.warn('User does not have a valid realtime token. Disconnecting WebSocket.');
          this.disconnect();
        }
      });
  }

  private initializeConnection() {
    if (this.authService.isAuthenticated) {
      const token = this.authService.getRealtimeToken();
      if (token) {
        void this.connect(token);
      }
    }
  }

  public async connect(token: string) {
    if (!(await this.featureFlagService.isFeatureFlagEnabled(FeatureFlagsEnum.InfraRealtime))) {
      logger.warn('Feature flag InfraRealtime is not enabled. Skipping connection.');
      return;
    }

    if (this.ws) {
      logger.warn('WebSocket connection already exists. Skipping connect.');
      return;
    }

    logger.info(
      'Attempting to connect to WebSocket with token:',
      token.slice(0, 6) + '**************' + token.slice(-6),
    );

    this.connectionState = 'connecting';
    this.ws = new WebSocket(environment.ISENGARD_REALTIME_URL);

    this.ws.onopen = this.onConnect.bind(this);
    this.ws.onmessage = this.onMessage.bind(this);
    this.ws.onerror = this.onError.bind(this);
    this.ws.onclose = this.onClose.bind(this);
  }

  public disconnect() {
    logger.info('Disconnecting from WebSocket.');
    if (this.ws) {
      this.ws.close();
    }

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  private onConnect() {
    logger.info('Successfully connected to WebSocket.');
    this.connectionState = 'connected';
    this.retryAttempts = 0; // Reset retry attempts on successful connection
    this.debugConnectionDetails.push({
      connectedAt: new Date(),
      firstMessageAt: null,
      disconnectedAt: null,
      lastMessageAt: null,
    });
  }

  private onMessage(event: MessageEvent) {
    try {
      const rawMessage = event.data as string;
      eventLogger.info('Received message from server:', rawMessage);

      const lastConnection = this.debugConnectionDetails[this.debugConnectionDetails.length - 1];
      if (!lastConnection.firstMessageAt) {
        lastConnection.firstMessageAt = new Date();
      }
      if (lastConnection) {
        lastConnection.lastMessageAt = new Date();
      }

      const data = safeJsonParse<{ op: RealtimeEventType; data: any }>(rawMessage);
      if (!data || !data.op || !data.data) {
        eventLogger.warn('Invalid message format received:', rawMessage);
        return;
      }

      this.debugMessages.push(data);
      this.manageMemory();

      this.handleLifecycleEvents(data.op, data);

      const eventType = data.op as RealtimeEventType;
      eventLogger.info('Event type received:', eventType);
      const eventInstance = new REALTIME_EVENT_MAP[eventType](data.data);

      const hasFeatureFlag = this.featureFlagService.isFeatureFlagEnabled(
        FeatureFlagsEnum.InfraRealtime,
      );
      if (!hasFeatureFlag) {
        logger.warn('Feature flag InfraRealtime is not enabled. Ignoring event:', eventType);
        return;
      }

      this.eventReceived$.next({
        op: eventType,
        data: eventInstance,
      });

      eventLogger.info('Event processed and emitted:', eventType, eventInstance);
    } catch (error) {
      eventLogger.error('Error parsing message:', error);
    }
  }

  private onError(error: Event) {
    logger.error('WebSocket error occurred:', error);
    this.onClose();
  }

  private onClose() {
    logger.warn('WebSocket connection closed.');
    this.connectionState = 'disconnected';

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    if (this.ws) {
      this.ws = null;
    }

    const lastConnection = this.debugConnectionDetails[this.debugConnectionDetails.length - 1];
    if (lastConnection) {
      lastConnection.disconnectedAt = new Date();
    }

    // Attempt to reconnect if max retry attempts not reached
    this.attemptReconnect();
  }

  private attemptReconnect() {
    if (!this.authService.isAuthenticated || this.retryAttempts >= this.maxRetryAttempts) {
      logger.warn('Max retry attempts reached or user not authenticated. Aborting reconnection.');
      return;
    }

    if (environment.ENVIRONMENT === 'development') {
      logger.warn('Reconnection attempts are disabled in development mode.');
      return;
    }

    this.retryAttempts++;

    const backoffTime = (this.retryAttempts + 1) * 5000 + Math.random() * 1000; // Add jitter
    logger.info(
      `Attempting reconnection (${this.retryAttempts}/${this.maxRetryAttempts}) in ${backoffTime}ms.`,
    );

    setTimeout(() => {
      const token = this.authService.getRealtimeToken();
      if (token) {
        void this.connect(token);
        for (const subs of this.subscriptions) {
          this.subscribe(subs as FluidPrefixType);
        }
      }
    }, backoffTime);
  }

  private sendHeartbeat() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        new ClientHeartbeatEvent({
          evt_id: this.generateEventId(),
          token: this.authService.getRealtimeToken() ?? '',
        }).toJSON(),
      );
    }
  }

  public subscribe(fluid: FluidPrefixType) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.warn('WebSocket is not open. Cannot subscribe.');
      this.queue.push(() => this.subscribe(fluid));
      return;
    }

    const evtId = this.generateEventId();

    this.ws.send(
      new ClientSubscribeEvent({
        evt_id: evtId,
        add: [fluid],
      }).toJSON(),
    );

    this.subscribeEvents[evtId] = {
      id: evtId,
      type: 'add',
      fluids: [fluid],
    };
  }

  public unsubscribe(fluid: FluidPrefixType) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      logger.warn('WebSocket is not open. Cannot unsubscribe.');
      this.queue.push(() => this.unsubscribe(fluid));
      return;
    }

    const evtId = this.generateEventId();

    this.ws.send(
      new ClientUnsubscribeEvent({
        evt_id: evtId,
        remove: [fluid],
      }).toJSON(),
    );

    this.subscribeEvents[evtId] = {
      id: evtId,
      type: 'remove',
      fluids: [fluid],
    };
  }

  public generateDebugReport() {
    return {
      debugMessages: this.debugMessages,
      retryAttempts: this.retryAttempts,
      maxRetryAttempts: this.maxRetryAttempts,
      debugConnectionDetails: this.debugConnectionDetails,
      connectionState: this.connectionState,
      subscriptions: Array.from(this.subscriptions),
    };
  }

  private generateEventId() {
    // Generate a random 14-digit number as a string
    return String(
      Math.floor(Math.random() * (99999999999999 - 10000000000000 + 1)) + 10000000000000,
    );
  }

  private manageMemory() {
    const maxMessages = 100;
    const maxConnections = 10;

    if (this.debugMessages.length > maxMessages) {
      this.debugMessages.splice(0, this.debugMessages.length - maxMessages);
    }

    if (this.debugConnectionDetails.length > maxConnections) {
      this.debugConnectionDetails.splice(0, this.debugConnectionDetails.length - maxConnections);
    }
  }

  private async handleLifecycleEvents(op: RealtimeEventType, receivedEvent: { data: any }) {
    eventLogger.info('Event received.', {
      op,
      data: receivedEvent.data,
    });

    if (op === 'SystemConnect') {
      logger.info('SystemConnect event received. Sending ClientAuthEvent.');
      this.ws?.send(
        new ClientAuthEvent({
          evt_id: this.generateEventId(),
          token: this.authService.getRealtimeToken() ?? '',
        }).toJSON(),
      );
    }

    if (op === 'SystemHello') {
      logger.info('SystemHello event received. Initializing subscriptions.');
      const event = new SystemHelloEvent(receivedEvent.data);

      this.subscriptions = new Set(event.payload?.init_subs);

      this.helloId = this.generateEventId();

      this.ws?.send(
        new ClientHelloEvent({
          evt_id: this.helloId,
          token: this.authService.getRealtimeToken() ?? '',
          client_version: environment.GRAPHQL_CLIENT_VERSION,
          init_subs: Array.from(this.subscriptions),
        }).toJSON(),
      );
      logger.info('Sent ClientHello event.');

      this.interval = setInterval(() => {
        this.sendHeartbeat();
      }, receivedEvent.data.heartbeat_interval);
    }

    if (op === 'SystemHeartbeat') {
      logger.info('SystemHeartbeat event received. Sending heartbeat response.');
      this.sendHeartbeat();
    }

    if (op === RealtimeEventType.SystemAcknowledge) {
      logger.info('SystemAcknowledge event received. Processing acknowledgment.');
      const event = new SystemAckEvent(receivedEvent.data);
      const evtId = event.data.ack_id;

      if (!evtId) {
        logger.warn('No evt_id found in SystemAckEvent.');
        return;
      }

      if (this.helloId === evtId) {
        logger.info('Hello event acknowledged. Flushing queue.');
        for (const item of this.queue) {
          logger.info('Processing queued item.');
          item(this);
        }
        this.queue = [];
        return;
      }

      const subscriptionEvent = this.subscribeEvents[evtId];
      if (subscriptionEvent) {
        switch (subscriptionEvent.type) {
          case 'add':
            this.subscriptions.add(subscriptionEvent.fluids[0]);
            break;
          case 'remove':
            this.subscriptions.delete(subscriptionEvent.fluids[0]);
            break;
        }

        logger.info(
          `Subscription ${subscriptionEvent.type} for fluid ${subscriptionEvent.fluids[0]} with evt_id ${evtId} (committed).`,
        );

        Reflect.deleteProperty(this.subscribeEvents, evtId); // Clean up after processing
      }
    }

    if (op === 'SystemError') {
      logger.error('SystemError event received:', receivedEvent.data);
      const event = new SystemErrorEvent(receivedEvent.data);

      const evtId = event.payload?.ack_id;
      if (!evtId) {
        logger.warn('No evt_id found in SystemErrorEvent.');
        return;
      }

      const subscriptionEvent = this.subscribeEvents[evtId];
      if (subscriptionEvent) {
        Reflect.deleteProperty(this.subscribeEvents, evtId); // Clean up after processing
      }
    }
  }
}
